import { Button } from "@chakra-ui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import Mustache from "mustache";
import React, { createContext, ReactNode, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "src/components/EmptyState";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import LaunchAppSvg from "src/images/launch-app.svg?react";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import * as Url from "src/services/url";
import { useStartForm } from "../forms/hooks/useStartForm";

type Item = {
  assignedForm: FSR.AssignedForm;
  nextStep: NonNullable<FSR.NextStep>;
};

interface NextStepContextType {
  process: (assignedForm: FSR.AssignedForm[] | undefined) => void;
}

const NextStepContext = createContext<NextStepContextType | undefined>(
  undefined
);

interface NextStepProviderProps {
  children: ReactNode;
}

export const NextStepProvider: React.FC<NextStepProviderProps> = ({
  children,
}) => {
  const [nextSteps, setNextSteps] = React.useState<readonly Item[]>([]);
  const nextStep = nextSteps[0];
  const popNextSteps = React.useCallback(() => {
    setNextSteps((state) => state.slice(1));
  }, []);

  const process = React.useCallback(
    (assignedForms: FSR.AssignedForm[] | undefined) => {
      if (!assignedForms) return;
      setNextSteps((state) => [
        ...state,
        ...assignedForms.flatMap((assignedForm) => {
          if (assignedForm.nextStep === null) return [];
          return [{ assignedForm, nextStep: assignedForm.nextStep }];
        }),
      ]);
    },
    []
  );

  return (
    <NextStepContext.Provider value={{ process }}>
      {children}
      <NextStepModal item={nextStep} next={popNextSteps} />
    </NextStepContext.Provider>
  );
};

export const useNextStep = (): NextStepContextType => {
  const context = useContext(NextStepContext);
  if (!context) {
    throw new Error("useNextStep must be used within a NextStepProvider");
  }
  return context;
};

type Props = {
  item: Item | undefined;
  next: () => void;
};
const NextStepModal: React.FC<Props> = ({ item, next }) => {
  const [isOpen, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (item !== undefined) {
      // open the dialog when we have new nextStep
      setOpen(true);
    }
  }, [item]);

  const onClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const nextStep = item?.nextStep;
  const title = nextStep
    ? Mustache.render(nextStep.title, nextStep.mustacheContext)
    : "";

  const subtitle = nextStep
    ? Mustache.render(nextStep.subtitle, nextStep.mustacheContext)
    : "";

  const description = nextStep
    ? Mustache.render(nextStep.body, nextStep.mustacheContext)
    : "";

  const buttonText = nextStep
    ? Mustache.render(nextStep.buttonText, nextStep.mustacheContext)
    : "";

  const { startAssignedForm } = useStartForm();
  const navigate = useNavigate();
  const [startingForm, setStartingForm] = React.useState(false);

  const organization = useOrganization();
  const toast = useAvelaToast();
  const startForm = React.useCallback(async () => {
    const assignedForm = item?.assignedForm;
    if (!organization.hasData() || !assignedForm) {
      return;
    }
    try {
      setStartingForm(true);
      const nextFormId = item.nextStep.nextFormId;
      if (nextFormId !== null) {
        // if form already exists, navigate to that form instead.
        onClose();
        setStartingForm(false);
        navigate(Url.Parent.Form.edit(organization.data, nextFormId));
        return;
      }

      const formId = await startAssignedForm({
        id: assignedForm.id,
        form_template: {
          id: assignedForm.formTemplateId,
        },
        person_id: assignedForm.personId,
        previous_form_id: assignedForm.previousFormId,
        previous_offer_id: assignedForm.previousOfferId,
        previous_waitlist_id: assignedForm.previousWaitlistId,
      });

      if (formId) {
        onClose();
        setStartingForm(false);
        navigate(Url.Parent.Form.edit(organization.data, formId));
      }
    } catch (e) {
      toast.error({
        title: "Oops! Something went wrong",
      });
      setStartingForm(false);
    }
  }, [
    item?.assignedForm,
    item?.nextStep.nextFormId,
    organization,
    startAssignedForm,
    navigate,
    onClose,
    toast,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} onCloseComplete={next}>
      <ModalOverlay />
      <ModalContent className={WEGLOT_APPLY_CLASS}>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>
          <EmptyState
            Svg={LaunchAppSvg}
            heading={subtitle}
            description={description}
          />
        </ModalBody>
        <ModalFooter
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          gap="2"
        >
          <Button variant="ghost" onClick={onClose}>
            Come back later
          </Button>
          <Button onClick={startForm} isLoading={startingForm}>
            {buttonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
